import apiService from './apiService';
import { v1Api } from './api';
import { API_CONFIG, getV1EndpointUrl } from '../config/api';

/**
 * Pricing Service
 * Handles all pricing configuration and fare calculation API calls
 */

const pricingService = {
  /**
   * Get tenant pricing configuration
   * @param {string} operatorId - Fleet operator ID
   * @returns {Promise} Pricing configuration data
   */
  async getTenantPricing(operatorId) {
    try {
      const response = await apiService.get(`/pricing/config/${operatorId}`);
      return response;
    } catch (error) {
      console.error('Error fetching tenant pricing:', error);
      throw error;
    }
  },

  /**
   * Create or update tenant pricing configuration
   * @param {string} operatorId - Fleet operator ID
   * @param {Object} pricingData - Pricing configuration data
   * @returns {Promise} Updated pricing configuration
   */
  async updateTenantPricing(operatorId, pricingData) {
    try {
      const response = await apiService.post(`/pricing/config/${operatorId}`, pricingData);
      return response;
    } catch (error) {
      console.error('Error updating tenant pricing:', error);
      throw error;
    }
  },

  /**
   * Calculate fare estimate (legacy)
   * @param {Object} fareData - Fare calculation parameters
   * @returns {Promise} Fare breakdown
   */
  async calculateFare(fareData) {
    try {
      const response = await apiService.post('/pricing/calculate-fare', fareData);
      return response;
    } catch (error) {
      console.error('Error calculating fare:', error);
      throw error;
    }
  },

  /**
   * Fare Estimate V2 - POST /v1/pricing/calculate-fare-v2
   * Request must include: operatorId, hospitalId (strongly recommended), ambulanceTypeId, distance, duration.
   * @param {Object} params
   * @param {string} params.operatorId - Fleet operator ID
   * @param {string} [params.hospitalId] - Hospital ID (strongly recommended; helps city resolution + contract)
   * @param {string} params.ambulanceTypeId - Ambulance type ID
   * @param {number} params.distance - Distance (km)
   * @param {number} [params.duration] - Duration (minutes)
   * @returns {Promise<{ total, breakdown, pricing_source, model }>}
   */
  async calculateFareV2({ operatorId, hospitalId, ambulanceTypeId, distance, duration }) {
    try {
      const endpoint = getV1EndpointUrl(API_CONFIG.PRICING_ENDPOINTS.CALCULATE_FARE_V2);
      const payload = {
        operatorId: String(operatorId),
        ambulanceTypeId: String(ambulanceTypeId),
        distance: Number(distance),
      };
      if (hospitalId != null && hospitalId !== '') payload.hospitalId = String(hospitalId);
      if (duration != null && duration !== '') payload.duration = Number(duration);
      const response = await v1Api.post(endpoint, payload);
      return response?.data?.data ?? response?.data ?? response;
    } catch (error) {
      console.error('Error calculating fare V2:', error);
      throw error;
    }
  },
};

export default pricingService;
